import React from 'react';
import { Link } from 'react-router-dom';
import { BuildingOffice2Icon, UsersIcon, LightBulbIcon, GlobeAltIcon, CheckBadgeIcon, AcademicCapIcon, EyeIcon } from '@heroicons/react/24/outline';

const PageHeader = ({ title, subtitle }) => (
  <div className="bg-figma-dark-bg-secondary py-16 md:py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-figma-dark-text-primary animate-fade-in-up">{title}</h1>
      {subtitle && <p className="mt-4 text-lg text-figma-dark-text-secondary max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.1s'}}>{subtitle}</p>}
    </div>
  </div>
);

const ValueCard = ({ icon: Icon, title, children, animationDelay }) => (
  <div className="bg-figma-dark-bg-secondary p-6 rounded-lg border border-figma-dark-border shadow-lg hover:shadow-xl transition-shadow hover:animate-subtle-3d-tilt-hover animate-fade-in-up" style={{ animationDelay }}>
    <Icon className="h-10 w-10 text-figma-dark-primary mb-4" />
    <h3 className="text-lg font-semibold text-figma-dark-text-primary mb-2 group-hover:text-figma-dark-primary transition-colors">{title}</h3>
    <p className="text-base text-figma-dark-text-secondary leading-relaxed">{children}</p>
  </div>
);

const AboutPage = () => {
  return (
    <div className="bg-figma-dark-bg-primary">
      <PageHeader 
        title="About YourCompany"
        subtitle="We are a team of innovators, thinkers, and creators, pioneering advancements in AI, Robotics, and Software to shape a smarter, more efficient future."
      />
      
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg shadow-2xl overflow-hidden animate-fade-in-up">
            <img 
              src="https://placehold.co/1200x500/16161A/7F5AF0?text=Our+Innovation+Hub&font=montserrat"
              alt="Modern and collaborative innovation hub of YourCompany"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Our Mission & Vision Section - Figma Dark Style */}
      <section className="py-12 md:py-20 bg-figma-dark-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h2 className="text-3xl font-bold text-figma-dark-text-primary mb-5">Our Mission</h2>
              <p className="text-lg text-figma-dark-text-secondary mb-4 leading-relaxed">
                To empower businesses with intelligent and autonomous solutions, leveraging cutting-edge AI and robotics to solve complex challenges, drive innovation, and create sustainable value. 
              </p>
              <p className="text-lg text-figma-dark-text-secondary leading-relaxed">
                We are committed to delivering excellence and fostering a culture of continuous learning and improvement.
              </p>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <h2 className="text-3xl font-bold text-figma-dark-text-primary mb-5">Our Vision</h2>
              <p className="text-lg text-figma-dark-text-secondary mb-4 leading-relaxed">
                To be a global leader in transformative technologies, shaping a future where intelligent systems seamlessly integrate with human potential to overcome the world's greatest challenges.
              </p>
              <p className="text-lg text-figma-dark-text-secondary leading-relaxed">
                We envision a future powered by responsible AI and collaborative robotics, driving progress for all.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section - Figma Dark Style */}
      <section className="py-12 md:py-20 bg-figma-dark-bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16 animate-fade-in-up">
            <h2 className="text-3xl font-bold text-figma-dark-text-primary">Our Core Values</h2>
            <p className="mt-4 text-lg text-figma-dark-text-secondary max-w-2xl mx-auto">
              The principles that guide our work, define our culture, and drive our commitment to clients.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <ValueCard icon={LightBulbIcon} title="Pioneering Innovation" animationDelay="0.1s">
              We foster a culture of relentless curiosity and continuous learning, pushing the boundaries to solve complex problems with novel solutions.
            </ValueCard>
            <ValueCard icon={CheckBadgeIcon} title="Uncompromising Excellence" animationDelay="0.2s">
              We are committed to the highest standards of quality, precision, and performance in everything we design, develop, and deliver.
            </ValueCard>
            <ValueCard icon={UsersIcon} title="Collaborative Partnership" animationDelay="0.3s">
              We believe in building strong, transparent relationships with our clients, working as an extension of their team to achieve shared success.
            </ValueCard>
            <ValueCard icon={GlobeAltIcon} title="Ethical Integrity" animationDelay="0.4s">
              We operate with unwavering honesty, transparency, and a strong sense of ethical responsibility in all our interactions and technological deployments.
            </ValueCard>
            <ValueCard icon={AcademicCapIcon} title="Continuous Growth" animationDelay="0.5s">
              The tech landscape evolves rapidly, and so do we. We champion ongoing learning and professional development for our team.
            </ValueCard>
             <ValueCard icon={EyeIcon} title="Impactful Vision" animationDelay="0.6s">
              We focus on creating solutions that deliver tangible results, meaningful impact, and lasting value for our clients and their end-users.
            </ValueCard>
          </div>
        </div>
      </section>

      {/* Team Section - Figma Dark Style */}
      <section className="py-16 md:py-20 bg-figma-dark-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in-up mb-12 md:mb-16">
              <h2 className="text-3xl font-bold text-figma-dark-text-primary mb-4">
                Meet the Minds Behind the Innovation
              </h2>
              <p className="text-lg text-figma-dark-text-secondary max-w-2xl mx-auto">
                Our diverse team of engineers, researchers, and strategists are the driving force behind our success, passionate about technology and its potential to transform industries.
              </p>
          </div>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <img 
                src="https://placehold.co/550x400/242630/2CB67D?text=Dynamic+Team+Collaboration&font=montserrat"
                alt="Diverse team of professionals collaborating at YourCompany in a modern dark setting"
                className="rounded-lg shadow-2xl w-full h-auto object-cover"
              />
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}> 
              <p className="text-lg text-figma-dark-text-secondary mb-8 leading-relaxed">
                We cultivate an environment where creativity flourishes and groundbreaking ideas are brought to life. Our experts bring a wealth of experience and a shared commitment to delivering outstanding results. 
              </p>
              <Link
                to="/careers" 
                className="inline-flex items-center bg-figma-dark-primary text-white font-semibold px-8 py-3.5 rounded-lg text-base hover:bg-figma-dark-primary-hover transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Join Our Team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage; 